import React from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import StarOutlined from "@mui/icons-material/StarOutlined";
import TipsAndUpdatesOutlined from "@mui/icons-material/TipsAndUpdatesOutlined";
import { MODEL_TIER_LABELS } from "../categories";

const TIER_CHIP = {
  best: { color: "#0F6E56", bg: "#E1F5EE", border: "#5DCAA5" },
  alt: { color: "#185FA5", bg: "#E6F1FB", border: "#85B7EB" },
  budget: { color: "#854F0B", bg: "#FAEEDA", border: "#EF9F27" },
};

function TierBadge({ tier, lang }) {
  const meta = MODEL_TIER_LABELS[tier] || MODEL_TIER_LABELS.alt;
  const label = lang?.[meta.key] || meta.fallback;
  const style = TIER_CHIP[tier] || TIER_CHIP.alt;
  return (
    <Chip
      size="small"
      icon={tier === "best" ? <StarOutlined sx={{ fontSize: "14px !important" }} /> : undefined}
      label={label}
      sx={{
        height: 20,
        fontSize: "0.65rem",
        fontWeight: 700,
        ml: 1,
        color: style.color,
        bgcolor: style.bg,
        border: `1px solid ${style.border}`,
        "& .MuiChip-icon": { color: style.color },
      }}
    />
  );
}

export default function ModelSelector({ value, onChange, models, note, lang }) {
  const theme = useTheme();
  const selected = models.find((m) => m.id === value) || null;

  return (
    <Stack spacing={1.25}>
      <Autocomplete
        size="small"
        options={models}
        value={selected}
        onChange={(_, option) => {
          if (option?.id) onChange(option.id);
        }}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, val) => option?.id === val?.id}
        filterOptions={(options, state) => {
          const q = String(state.inputValue || "")
            .trim()
            .toLowerCase();
          if (!q) return options;
          return options.filter((m) => {
            const label = String(m.label || "").toLowerCase();
            const id = String(m.id || "").toLowerCase();
            const tier = String(MODEL_TIER_LABELS[m.tier]?.fallback || "").toLowerCase();
            return label.includes(q) || id.includes(q) || tier.includes(q);
          });
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.id}
            sx={{ display: "flex", alignItems: "center", width: "100%", py: 1 }}
          >
            <Typography variant="body2" fontWeight={option.tier === "best" ? 700 : 500}>
              {option.label}
            </Typography>
            <TierBadge tier={option.tier} lang={lang} />
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={lang?.videoStudioModelLabel || "Model"}
            placeholder={lang?.videoStudioModelSearch || "Search models…"}
          />
        )}
        noOptionsText={lang?.videoStudioNoModels || "No models found"}
        disableClearable
      />

      {note && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.info.main, 0.08),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          }}
        >
          <TipsAndUpdatesOutlined sx={{ fontSize: 18, color: "info.main", mt: 0.15 }} />
          <Typography variant="caption" color="text.secondary" lineHeight={1.55}>
            {note}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
